import React, { useState, useRef, useEffect } from "react";
import {
  FaThLarge,
  FaUserGraduate,
  FaBullhorn,
  FaClipboardList,
  FaEnvelopeOpenText,
  FaCalendarCheck,
  FaChalkboardTeacher,
  FaEdit,
  FaUserShield,
  FaSignOutAlt,
  FaBars,
  FaFileAlt,
  FaChartLine,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";
import { useLocation } from "react-router-dom";
import "./Menu.css";

function Menu({ onMenuClick, isOpen, toggleMenu }) {
  const [activeSection, setActiveSection] = useState(
    localStorage.getItem("activePage") || "Overview"
  );
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isAdmissionsOpen, setIsAdmissionsOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => {
    const storedPage = localStorage.getItem("activePage");
    if (storedPage) setActiveSection(storedPage);
  }, [location.pathname]);

  useEffect(() => {
    const menuEl = menuRef.current;
    if (menuEl) {
      menuEl.style.overflow = isOpen ? "auto" : "hidden";
    }
    return () => {
      if (menuEl) menuEl.style.overflow = "auto";
    };
  }, [isOpen]);

  const closeDropdowns = () => {
    setIsReportsOpen(false);
    setIsAdmissionsOpen(false);
  };

  const handleMenuItemClick = (section, isSubItem = false) => {
    if (section === "Logout") {
      const confirmLogout = window.confirm("Are you sure you want to log out?");
      if (confirmLogout) {
        localStorage.clear();
        sessionStorage.clear();
        setActiveSection("Overview");
        closeDropdowns();
        window.location.href = "/login";
      }
      return;
    }

    if (section === "PrivacyPolicy" || section === "TermsOfUse") {
      onMenuClick(section);
      return;
    }

    if (section === "Reports" && !isSubItem) {
      setIsReportsOpen(prev => !prev);
      return;
    }

    if (section === "Admissions" && !isSubItem) {
      setIsAdmissionsOpen(prev => !prev);
      return;
    }

    const pageMap = {
      Overview: "Overview",
      Reports: "Reports",
      Admin: "Admin",
      Student: "Student",
      Teacher: "Teacher",
      Enquiries: "Enquiries",
      AdmissionList: "AdmissionList",
      AdmissionsList: "AdmissionsList",
      WithdrawalList: "WithdrawalList",
      "Attendance Report": "AttendanceReport",
      "Assignment Report": "AssignmentReport",
      "Result Report": "ResultReport",
      "Performance Report": "PerformanceReport"
    };

    const selected = pageMap[section] || section;
    setActiveSection(isSubItem ? section : selected);
    localStorage.setItem("activePage", selected);
    onMenuClick(selected);
    closeDropdowns();
  };

  const menuItems = [
    { name: "Admin", icon: <FaUserShield /> },
    { name: "Overview", icon: <FaThLarge />, label: "Overview" },
    // AdmissionList main menu item REMOVED here
    { name: "Student", icon: <FaUserGraduate /> },
    { name: "Teacher", icon: <FaChalkboardTeacher /> },
    { name: "Announcement", icon: <FaBullhorn /> },
    { name: "Assignment", icon: <FaClipboardList /> },
    { name: "Circular", icon: <FaEnvelopeOpenText /> },
    { name: "Attendance", icon: <FaCalendarCheck /> },
    {
      name: "Admissions",
      icon: <FaEdit />,
      label: "Admissions",
      subItems: [
        { name: "Enquiries", label: "Enquiries", icon: <FaEnvelopeOpenText /> },
        // Application REMOVED here
        { name: "AdmissionsList", label: "Admission List", icon: <FaClipboardList /> },
        { name: "WithdrawalList", label: "Withdrawal List", icon: <FaClipboardList /> }
      ]
    },
    {
      name: "Reports",
      icon: <FaFileAlt />,
      label: "Reports",
      subItems: [
        { name: "Attendance Report", label: "Attendance Report", icon: <FaCalendarCheck /> },
        { name: "Assignment Report", label: "Assignment Report", icon: <FaClipboardList /> },
        { name: "Result Report", label: "Result Report", icon: <FaFileAlt /> },
        { name: "Performance Report", label: "Performance Report", icon: <FaChartLine /> }
      ]
    },
    { name: "Logout", icon: <FaSignOutAlt />, label: "Logout" }
  ];

  return (
    <>
      {isOpen && (
        <div className="menu-container">
          <div ref={menuRef} className={`menu expanded ${isOpen ? "active" : ""}`}>
            <div className="menu-links">
              {menuItems.map(({ name, icon, label, subItems }) => (
                <div key={name} className="menu-item-wrapper">
                  <div
                    className={`menu-item ${activeSection === name ? "active" : ""}`}
                    onClick={() => handleMenuItemClick(name)}
                  >
                    <span className="menu-icon">{icon}</span>
                    <span className="menu-text">{label || name}</span>
                    {subItems && (
                      <span className="dropdown-icon">
                        {(name === "Reports" && isReportsOpen) ||
                        (name === "Admissions" && isAdmissionsOpen) ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </span>
                    )}
                  </div>

                  {/* Submenu for Reports */}
                  {subItems && name === "Reports" && isReportsOpen && (
                    <div className="submenu">
                      {subItems.map((subItem) => (
                        <div
                          key={subItem.name}
                          className={`submenu-item ${activeSection === subItem.name ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuItemClick(subItem.name, true);
                          }}
                        >
                          <span className="menu-icon">{subItem.icon}</span>
                          <span className="submenu-text">{subItem.label}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Submenu for Admissions */}
                  {subItems && name === "Admissions" && isAdmissionsOpen && (
                    <div className="submenu">
                      {subItems.map((subItem) => (
                        <div
                          key={subItem.name}
                          className={`submenu-item ${activeSection === subItem.name ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuItemClick(subItem.name, true);
                          }}
                        >
                          <span className="menu-icon">{subItem.icon}</span>
                          <span className="submenu-text">{subItem.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <button className="menu-toggle-btn" onClick={toggleMenu}>
        <FaBars />
      </button>
    </>
  );
}

export default Menu;
